import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TranslationService } from './translation.service';
import { FilterMap } from '../models/filter-map.model';
import { SearchResponse } from '../models/search-response.model';
import { TrendingTerm } from '../models/trending-term.model';
import {
  RESULTS_PER_PAGE,
  SOLR_DEFAULT_PARAMS,
  SOLR_FIELD_LIST,
  SOLR_FIELD_MAPPING,
  SOLR_JSON_FACET_CONFIG
} from '../constants/solr.constants';
import { WIDGET_CONFIG } from '../utils/widget-config.util';

@Injectable({ providedIn: 'root' })
export class SearchService {
  private readonly apiBaseUrl = '';
  private readonly apiBasePath = '/search';

  constructor(
    private readonly http: HttpClient,
    private readonly translationService: TranslationService
  ) {}

  search(
    query: string,
    page: number,
    filters: FilterMap,
    options: { rows?: number; sort?: string } = {}
  ): Observable<SearchResponse> {
    const params = this.buildSolrSearchParams(query, page, filters, options);
    return this.http.get<SearchResponse>(this.buildApiUrl('search'), { params });
  }

  /**
   * Trimite un semnal fire-and-forget cand userul deschide un rezultat
   * afisat pentru o cautare. Nu blocheaza navigarea si nu raporteaza erori -
   * e doar telemetrie, nu trebuie sa afecteze experienta de cautare.
   */
  trackClick(query: string, resultId: string, resultType?: string, position?: number): void {
    if (!query || !query.trim()) {
      return;
    }
    this.http
      .post(this.buildApiUrl('click'), { q: query.trim(), resultId, resultType, position })
      .subscribe({ error: () => undefined });
  }

  getTrending(limit = 8): Observable<{ items: TrendingTerm[] }> {
    return this.http.get<{ items: TrendingTerm[] }>(this.buildApiUrl('trending'), {
      params: new HttpParams().set('limit', String(limit))
    });
  }

  getContext(): Observable<{ lang: string }> {
    let params = new HttpParams();
    const urlLang = this.getUrlLanguage();
    if (urlLang) {
      params = params.set('lang', urlLang);
    }
    return this.http.get<{ lang: string }>(this.buildApiUrl('context'), { params });
  }

  getUrlLanguage(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    // Extract /ro or /en segment from URL path
    const match = window.location.pathname.match(/\/(ro|en)(?:\/|$)/i);
    return match ? match[1].toLowerCase() : null;
  }

  private buildSolrSearchParams(
    query: string,
    page: number,
    filters: FilterMap,
    options: { rows?: number; sort?: string } = {}
  ): HttpParams {
    const rows = options.rows ?? RESULTS_PER_PAGE;
    const start = page * rows;
    const lang = this.resolveLanguage();

    let params = new HttpParams()
      .set('q', this.buildSolrQueryString(query))
      .set('lang', lang)
      .set('rows', String(rows))
      .set('start', String(start))
      .set('fl', SOLR_FIELD_LIST)
      .set('json.facet', JSON.stringify(SOLR_JSON_FACET_CONFIG))
      .set('searchFields', WIDGET_CONFIG.searchFields);

    if (options.sort) {
      params = params.set('sort', options.sort);
    }

    Object.entries(SOLR_DEFAULT_PARAMS).forEach(([key, value]) => {
      params = params.set(key, value);
    });

    // Send filters as named parameters (e.g. cityId=123, countyId=456)
    // instead of raw fq params - server handles the SOLR field mapping
    Object.entries(filters).forEach(([facetKey, values]) => {
      if (!SOLR_FIELD_MAPPING[facetKey]) {
        return;
      }

      values
        .filter(value => value !== undefined && value !== null && String(value).trim() !== '')
        .forEach(value => {
          params = params.append(facetKey, String(value).trim());
        });
    });

    return params;
  }

  private buildSolrQueryString(rawQuery: string): string {
    if (!rawQuery || !rawQuery.trim()) {
      return '*:*';
    }

    const trimmed = rawQuery.trim();
    if (trimmed === '*:*') {
      return '*:*';
    }

    const tokens = trimmed
      .split(/\s+/)
      .map(token => token.replace(/\*+$/, ''))
      .map(token => this.escapeSolrTerm(token))
      .filter(Boolean)
      .map(token => `${token}*`);

    if (!tokens.length) {
      return '*:*';
    }

    return tokens.join(' ');
  }

  getSuggestions(query: string): Observable<string[]> {
    if (!query || query.length < 2) {
        return new Observable(observer => {
            observer.next([]);
            observer.complete();
        });
    }

    const lang = this.resolveLanguage();
    return this.http.get<string[]>(`${this.apiBaseUrl}${this.apiBasePath}/terms`, {
      params: new HttpParams().set('prefix', query).set('lang', lang)
    });
  }

  private resolveLanguage(): string {
    const urlLang = this.getUrlLanguage();
    const currentLang = this.translationService.getLanguage();
    // If the URL language changed (e.g. Liferay SPA navigation), sync TranslationService
    if (urlLang && urlLang !== currentLang) {
      this.translationService.setLanguage(urlLang);
    }
    return this.translationService.getLanguage() || urlLang || 'ro';
  }

  private escapeSolrTerm(term: string): string {
    return term.replace(/[+\-!(){}\[\]^"~*?:\\/]/g, '\\$&');
  }

  private formatSolrFilter(field: string, rawValue: string): string | null {
    if (rawValue === undefined || rawValue === null) {
      return null;
    }

    const value = String(rawValue).trim();
    if (!value || value === '__ALL__') {
      return null;
    }

    const escaped = value.replace(/(["\\])/g, '\\$1');
    const needsQuotes = /[\s:\(\){}\[\]]/.test(value);
    return needsQuotes ? `${field}:"${escaped}"` : `${field}:${escaped}`;
  }

  private buildApiUrl(path: string): string {
    const base = this.apiBaseUrl.replace(/\/$/, '');
    const prefix = this.apiBasePath.replace(/\/$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${prefix}${normalizedPath}`;
  }
}
